/**
 * `fetch` enrichi pour le mode démo. Branché une seule fois sur le client API
 * (`createVictorApi({ fetchFn })`). Hors mode démo : `fetch` natif, zéro impact.
 *
 * En mode démo :
 *  - les endpoints d'auth/session passent toujours en RÉEL (la session reste vraie) ;
 *  - un GET avec fixture → donnée fictive ; sinon → requête réelle (shape valide) ;
 *  - toute écriture (POST/PUT/PATCH/DELETE) est neutralisée (lecture seule) et
 *    déclenche un toast, sans jamais toucher la base.
 */
import { DEMO_READONLY_EVENT, isDemoActive } from "./demoMode";
import { matchFixture } from "./fixtures";

function jsonResponse(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

/**
 * Chemins TOUJOURS réels en mode démo : auth/session + tous les flux
 * d'onboarding critiques (signature de contrat + OTP, mandat SEPA,
 * abonnement). La démo ne doit jamais casser une vraie action du client.
 */
const ALWAYS_REAL = [
  "/v1/auth",
  "/v1/public",
  "/v1/me/contrats", // lecture + signature/OTP (/sign/send-code, /sign/verify…)
  "/v1/abonnement", // mandat SEPA / souscription
  "/v1/help", // centre d'aide (contenu global réel, OK en démo)
  "/v1/support", // tickets de support (réels, espace agence)
];

export const demoFetch: typeof fetch = (input, init) => {
  if (!isDemoActive()) return fetch(input, init);

  const method = (
    init?.method ??
    (input instanceof Request ? input.method : "GET")
  ).toUpperCase();
  const full = urlOf(input);
  const path = full.replace(/^https?:\/\/[^/]+/, "");

  // Flux critiques (auth, signature, mandat…) : toujours réels.
  if (ALWAYS_REAL.some((p) => path.startsWith(p))) {
    return fetch(input, init);
  }

  if (method === "GET") {
    const fixture = matchFixture(path);
    if (fixture !== undefined) {
      // Le client API attend l'enveloppe `{ data }`.
      return Promise.resolve(jsonResponse({ data: fixture }));
    }
    // Pas de fixture → donnée réelle (tenant vide mais shape garantie valide).
    return fetch(input, init);
  }

  // Écriture en mode démo : neutralisée (lecture seule). On renvoie l'enveloppe
  // attendue par le client API (`{ data }`) pour ne jamais lever d'erreur.
  window.dispatchEvent(new CustomEvent(DEMO_READONLY_EVENT));
  return Promise.resolve(jsonResponse({ data: { ok: true, demo: true } }));
};
